import { Injectable } from '@angular/core';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { from } from 'rxjs';
import { take } from 'rxjs/operators';
import { COLLECTION_MEDICATIONS } from '../global';
import { Medication } from '../models/medication.model';

@Injectable({
  providedIn: 'root',
})
export class MedicationService {
  constructor(private db: AngularFirestore) {}

  /**
   * Gets all medications from the "Medications" Firestore collection.
   *
   * @returns A snapshot of the entire Medications collection.
   */
  getAllMedications() {
    return this.db.collection(COLLECTION_MEDICATIONS).snapshotChanges();
  }

  /**
   * Gets a medication with the specified id from the "Medications" Firestore collection.
   *
   * @param medicationId: the id of the medication to get
   * @returns A snapshot of the medication.
   */
  getMedication(medicationId: string) {
    return this.db.collection(COLLECTION_MEDICATIONS).doc(medicationId).snapshotChanges();
  }

  static getMedicationFromSnapshot(snapshot: any): Medication {
    const medication = new Medication();
    const obj = {
      id: snapshot.payload.id,
      ...(snapshot.payload.data() as object),
    };
    Object.assign(medication, obj);
    return medication;
  }

  /**
   * Prepares the Medication object for Firestore before creating/updating it
   * in the backend.
   *
   * @param med The Medication object to create in Firestore.
   * @returns Observable
   */
  upsertMedication(med: Medication) {
    this.cleanMedData(med);
    const obj = this.extractMedData(med);

    // Use "set" method, since we separate the ID from the data; ID
    // is used as the document ID
    return from(this.db.collection(COLLECTION_MEDICATIONS).doc(med.id).set(obj));
  }

  /**
   *
   * @param id The ID of the medication to delete from Firestore.
   * @returns Observable
   */
  deleteMedication(id: string) {
    return from(this.db.collection(COLLECTION_MEDICATIONS).doc(id).delete()).pipe(take(1));
  }

  /**
   *
   * @param med The Medication object to remove the ID from.
   * @returns A copy of the Medication object with the ID parameter removed.
   */
  private extractMedData(med: Medication): object {
    // Convert Medication object to a JSON object
    const obj = JSON.parse(JSON.stringify(Object.assign({}, med)));
    // Do this since the ID will be the document name
    delete obj.id;

    return obj;
  }

  /**
   * Cleans the Medication object by ensuring no whitespaces are on either end
   * of the properties, and that there are no spaces in the ID.
   *
   * @param med The Medication object to clean the data for.
   */
  private cleanMedData(med: Medication) {
    med.id = med.id.replace(/\s/g, '');
    med.name = med.name.trim();
    med.routes.forEach((route) => route.trim());
  }
}
